/**
 * UUID v4 generado en el cliente.
 *
 * Sirve para dos cosas: llaves locales que viajan al servidor como `Guid`, y los Ids
 * con que se crean órdenes, renglones y pagos. Con esos últimos el servidor reconoce
 * un reintento (la petición se guardó pero la respuesta no llegó) y contesta con lo
 * ya guardado en vez de duplicarlo o dar un error falso.
 *
 * **Tiene que ser un UUID de verdad**, no una cadena cualquiera: del otro lado la
 * propiedad es un `Guid` y cualquier otra cosa hace fallar la deserialización.
 *
 * `crypto.randomUUID` no existe en contextos no seguros, y el punto de venta corre
 * más de una vez por http en la red del restaurante. Por eso el fallback arma un v4
 * a mano con `crypto.getRandomValues` (que sí existe ahí) y, en último caso, con
 * Math.random.
 */
export const newClientId = (): string => {
  const webCrypto: Crypto | undefined = typeof crypto !== 'undefined' ? crypto : undefined;
  if (typeof webCrypto?.randomUUID === 'function') {
    return webCrypto.randomUUID();
  }

  const bytes = new Uint8Array(16);
  if (typeof webCrypto?.getRandomValues === 'function') {
    webCrypto.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i++) bytes[i] = (Math.random() * 256) | 0;
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40; // versión 4
  bytes[8] = (bytes[8] & 0x3f) | 0x80; // variante RFC 4122

  const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
